import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "~/components/build/Show/components/Drawer";
import { useCallback } from "react";
import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import { RetryableJob } from "~/components/build/Show/lib/types";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useQueries } from "@tanstack/react-query";
import { useReportQueryError } from "~/components/build/Show/lib/useReportQueryError";
import { stepQueryOptions } from "~/components/build/Show/lib/queries/step";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";
import { jobRetriesQueryOptions } from "~/components/build/Show/lib/queries/jobRetries";
import { DrawerActions } from "~/components/build/Show/components/StepDrawer/StepDrawerActions";
import {
  shouldRenderJobDrawer,
  shouldRenderStepDrawer,
  JobContent,
  StepContent,
  resolveCurrentJob,
} from "~/components/build/Show/components/StepDrawer/resolveDrawerVariant";
import { StepDrawerProvider } from "~/components/build/Show/components/StepDrawer/StepDrawerContext";

function StepDrawerSkeleton() {
  return (
    <div>
      <div className="h-[44px] w-full animate-pulse rounded-sm border-b border-gray-500 bg-gray-200" />
      <div className="h-64 w-full animate-pulse rounded-sm bg-gray-200" />
    </div>
  );
}

export function LazyDataStepDrawer() {
  const { build } = useBuild();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useQueryParams();

  const jobQueryEnabled = Boolean(params.jobUuid) && params.open !== "false";
  const [jobFetchResult, retriesFetchResult] = useQueries({
    queries: [
      jobQueryOptions(build, params.jobUuid, { enabled: jobQueryEnabled, retry: false }),
      jobRetriesQueryOptions(build, params.jobUuid, { enabled: jobQueryEnabled, retry: false }),
    ],
  });

  // Fetch step and jobs in parallel using useQueries
  const stepQueryEnabled = Boolean(params.stepUuid) && params.open !== "false";
  const [stepResult, stepJobsResult] = useQueries({
    queries: [
      stepQueryOptions(build, params.stepUuid, { enabled: stepQueryEnabled, retry: false }),
      stepJobsQueryOptions(build, params.stepUuid, { enabled: stepQueryEnabled, retry: false }),
    ],
  });

  // Combine step and jobs results for compatibility with existing rendering logic
  const step = stepResult.data;
  const jobs = stepJobsResult.data || [];

  useReportQueryError(jobFetchResult.isError, jobFetchResult.error, "StepDrawer/job", {
    buildId: build.id,
    jobUuid: params.jobUuid,
  });

  const stepError = stepResult.error || stepJobsResult.error;
  useReportQueryError(stepResult.isError || stepJobsResult.isError, stepError, "StepDrawer/step", {
    buildId: build.id,
    stepUuid: params.stepUuid,
  });

  // Close the drawer and navigate back to the build view (ie. waterfall / canvas / summary).
  const handleClose = useCallback(() => {
    navigate(pathname);
  }, [pathname, navigate]);

  // The trigger/root step isn't a real step in the pipeline so for now we
  // just ignore it.
  if (params.stepUuid === "root") {
    return null;
  }

  if (params.open === "false") {
    return null;
  }

  if (params.jobUuid) {
    if (jobFetchResult.isError) {
      return (
        <Drawer onClose={handleClose}>
          <div className="mx-2.5">
            <ErrorView clearError={() => navigate(0)} />
          </div>
        </Drawer>
      );
    }

    const job = jobFetchResult.data as RetryableJob | undefined;
    const retries = (retriesFetchResult.data as RetryableJob[] | undefined) || [];

    return (
      <StepDrawerProvider job={job} jobs={[]} retries={retries}>
        <Drawer onClose={handleClose} actions={<DrawerActions />}>
          {job && shouldRenderJobDrawer(job) ? (
            <ErrorBoundary FallbackComponent={ErrorView}>
              <JobContent job={job} />
            </ErrorBoundary>
          ) : (
            <StepDrawerSkeleton />
          )}
        </Drawer>
      </StepDrawerProvider>
    );
  }

  if (params.stepUuid) {
    const hasError = stepResult.isError || stepJobsResult.isError;
    if (hasError) {
      return (
        <Drawer onClose={handleClose}>
          <div className="mx-2.5">
            <ErrorView clearError={() => navigate(0)} />
          </div>
        </Drawer>
      );
    }

    // Show skeleton while either query is loading
    if (!step || !stepJobsResult.data) {
      return (
        <Drawer onClose={handleClose}>
          <StepDrawerSkeleton />
        </Drawer>
      );
    }

    if (!shouldRenderStepDrawer(step)) {
      return null;
    }

    const currentJob = resolveCurrentJob(step, jobs);

    return (
      <StepDrawerProvider step={step} job={currentJob} jobs={jobs} retries={jobs as RetryableJob[]}>
        <Drawer onClose={handleClose} actions={<DrawerActions />}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <StepContent step={step} />
          </ErrorBoundary>
        </Drawer>
      </StepDrawerProvider>
    );
  }

  return null;
}
